import { format } from "date-fns";
import { AboMandateStatus } from "../types/AboMandateStatus";
import { BankTransferOptions } from "../types/BankTransferOptions";
import { Debtor } from "../types/Debtor";
import { PayGateUrl } from "../types/PayGateUrl";
import { PaymentFormData } from "../types/PaymentFormData";
import { PendingRateAgreement } from "../types/PendingRateAgreement";
import { RateAgreementConfirmation } from "../types/RateAgreementConfirmation";
import { RateAgreementInfo } from "../types/RateAgreementInfo";
import { RateAgreementOptions } from "../types/RateAgreementOptions";
import { RateAgreementTenantConfiguration } from "../types/RateAgreementTenantConfiguration";
import { RateData } from "../types/RateData";
import { SettlementFee } from "../types/SettlementFee";
import { ApiService } from "./ApiService";

const baseUrl = "/services/rateagreement/api/rateagreement";

class RateAgreementService extends ApiService {
    async createRateAgreement(
        rateData: RateData,
        paymentFormData: PaymentFormData,
        campaign: string | null,
        rateAgreementOptions: RateAgreementOptions
    ): Promise<RateAgreementConfirmation> {
        const rateDataRequest = { ...rateData };
        // @ts-ignore format to backend date format
        rateDataRequest.dateOfFirstPay = format(rateData.dateOfFirstPay as Date, "yyyy-MM-dd");

        const request = Object.assign(rateDataRequest, { payment: paymentFormData }, { campaignName: campaign }, { rateAgreementOptions: rateAgreementOptions });
        const response = await fetch(baseUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
        });

        const rateAgreementResponse = await this.handleResponse(response);
        return rateAgreementResponse.json();
    }

    async getRateAgreementOptions(debtNumber: string | undefined): Promise<RateAgreementOptions> {
        const response = await fetch(baseUrl + "/" + debtNumber + "/options", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        const responseOption = await this.handleResponse(response);
        const responseJson = await responseOption.json();
        responseJson.earliestPaymentDay = new Date(responseJson.earliestPaymentDay);
        responseJson.latestPaymentDay = new Date(responseJson.latestPaymentDay);
        responseJson.maxRate = Math.floor(responseJson.maxRate);
        responseJson.minRate = Math.ceil(responseJson.minRate);
        return responseJson as RateAgreementOptions;
    }

    /**
     * Checks whether rate agreement plan is possible for the debtNumber
     * @param debtNumber - the debt number of a given user
     * @return true if rate agreement is possible or false otherwise
     */
    async isRateAgreementAvailable(debtNumber: string): Promise<boolean> {
        const isRateAgreementAvailable = await fetch(baseUrl + `/${debtNumber}/available`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        const isRateAgreementAvailableResponse = await this.handleResponse(isRateAgreementAvailable);

        const response = await isRateAgreementAvailableResponse.text();
        return response === "true";
    }

    /**
     * Fetches the transfer information from backend
     * @param debtNumber - the debt number of a given user
     * @return BankTransferOptions object
     */
    async getBankTransferOptions(debtNumber: string): Promise<BankTransferOptions> {
        const response = await fetch(baseUrl + "/" + debtNumber + "/payment-options/bank-transfer", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        const debtorResponse = await this.handleResponse(response);
        return debtorResponse.json();
    }

    async getActiveRateAgreement(debtNumber: string): Promise<RateAgreementInfo | undefined> {
        const response = await fetch(baseUrl + "/" + debtNumber + "/active", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        const rateAgreement = await this.handleResponse(response);

        if (rateAgreement.status === 204) {
            return undefined;
        }

        return rateAgreement.json();
    }

    async getPendingAgreement(data: string): Promise<PendingRateAgreement> {
        const response = await fetch(baseUrl + "/pending?data=" + data, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });
        const rateAgreement = await this.handleResponse(response);
        return rateAgreement.json();
    }

    async getAboMandateStatus(data: string): Promise<AboMandateStatus> {
        const response = await fetch(baseUrl + "/abo-mandate-status?data=" + data, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        });

        const aboMandateStatus = await this.handleResponse(response);
        return aboMandateStatus.json();
    }

    async getPayGateUrl({ debtNo, rateAgreementId }: Pick<AboMandateStatus, "debtNo" | "rateAgreementId">): Promise<PayGateUrl> {
        const response = await fetch(`${baseUrl}/${debtNo}/pay-gate-url?rateAgreementId=${rateAgreementId}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        });

        const payGateUrl = await this.handleResponse(response);
        return payGateUrl.json();
    }

    async suspendRateAgreement(debtNumber: string, debtor: Debtor, rateAgreementId: string, suspend: boolean, consecutiveNumber?: number): Promise<void> {
        const request = { rateAgreementId: rateAgreementId, firstName: debtor.firstName, lastName: debtor.lastName, consecutiveNumber: consecutiveNumber, suspend: suspend };
        const response = await fetch(baseUrl + "/" + debtNumber + "/suspend", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(request),
        });

        await this.handleResponse(response);
    }

    async getSettlementFee(rateData: RateData): Promise<SettlementFee> {
        const response = await fetch(baseUrl + "/" + rateData.debtNumber + "/settlementfee", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                debtNumber: rateData.debtNumber,
                rateAmount: rateData.rateAmount,
                numberOfRates: rateData.rateNumber,
                dateOfFirstPay: rateData.dateOfFirstPay ? format(rateData.dateOfFirstPay, "yyyy-MM-dd") : null,
            }),
        });
        const settlementFeeResponse = await this.handleResponse(response);
        return settlementFeeResponse.json();
    }

    async getTenantConfiguration(): Promise<RateAgreementTenantConfiguration> {
        const response = await fetch(baseUrl + "/tenant-configuration", {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        const tenantResponse = await this.handleResponse(response);
        return tenantResponse.json();
    }
}

export default new RateAgreementService();
